import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, Subject, Subscription, interval } from 'rxjs';
import { AppService } from 'src/app/app.service';

export type SessionPhase = 'inactive' | 'active' | 'warning' | 'expired';

export interface SessionState {
  /** Current lifecycle phase, derived from time remaining before expiry. */
  phase: SessionPhase;
  /** Absolute time the session will expire if not extended. */
  sessionExpiresAt: string | null;
  /** Seconds-before-expiry at which the warning popup appears. */
  warningSeconds: number;
  /** Seconds remaining before the session expires. */
  remainingSeconds: number;
}

/**
 * Cookie-driven session tracker.
 *
 * The backend writes a JS-readable `session_expiry` cookie (epoch ms) on every
 * real authenticated API response, plus a `session_warn` cookie (warning lead
 * time in seconds). This service reads those cookies once per second and
 * derives a live countdown — no polling and no idle tracking.
 *
 * Because any real API call refreshes the cookie, ordinary app activity
 * automatically extends the countdown. When the countdown reaches zero the
 * phase becomes `expired` and the UI shows the "sign back in" popup.
 */
@Injectable({ providedIn: 'root' })
export class SessionService implements OnDestroy {
  private readonly EXPIRY_COOKIE = 'session_expiry';
  private readonly WARN_COOKIE = 'session_warn';

  private tickSub: Subscription | null = null;

  private stateSubject = new BehaviorSubject<SessionState>({
    phase: 'inactive',
    sessionExpiresAt: null,
    warningSeconds: 0,
    remainingSeconds: 0,
  });
  state$: Observable<SessionState> = this.stateSubject.asObservable();

  private logoutSubject = new Subject<void>();
  logout$: Observable<void> = this.logoutSubject.asObservable();

  constructor(private zone: NgZone, private appService: AppService) {}

  /** Begin the local countdown. Call once the user is authenticated. */
  start(): void {
    this.stop();
    this.tick();

    // 1s countdown (outside Angular to avoid change-detection churn).
    this.zone.runOutsideAngular(() => {
      this.tickSub = interval(1000).subscribe(() => {
        this.zone.run(() => this.tick());
      });
    });
  }

  /**
   * Explicitly extend the session ("Stay logged in"). A real API call
   * (GetLoggedInUser) refreshes the backend `session_expiry` cookie; the next
   * tick reads the new value and resets the countdown.
   */
  extend(): void {
    if (!this.tickSub) {
      this.start();
    }
    this.appService.getLoggedInUser().subscribe({
      next: () => this.tick(),
      error: () => this.tick(),
    });
  }

  /** Trigger logout / redirect ("Log out now" or "Sign back in"). */
  forceLogout(): void {
    this.stop();
    this.logoutSubject.next();
  }

  /** Stop the countdown. */
  stop(): void {
    this.tickSub?.unsubscribe();
    this.tickSub = null;
  }

  /** Read a cookie value by name, or null if absent. */
  private readCookie(name: string): string | null {
    const match = document.cookie
      .split('; ')
      .find((row) => row.startsWith(`${name}=`));
    return match ? decodeURIComponent(match.substring(name.length + 1)) : null;
  }

  private tick(): void {
    const expiryRaw = this.readCookie(this.EXPIRY_COOKIE);
    const warnRaw = this.readCookie(this.WARN_COOKIE);

    const expiresAt = expiryRaw ? Number(expiryRaw) : NaN;
    const warningSeconds = warnRaw ? Number(warnRaw) : 0;

    if (!expiryRaw || Number.isNaN(expiresAt)) {
      // No expiry cookie yet (not authenticated) — stay inactive.
      this.stateSubject.next({
        phase: 'inactive',
        sessionExpiresAt: null,
        warningSeconds: 0,
        remainingSeconds: 0,
      });
      return;
    }

    const remainingSeconds = Math.max(
      Math.round((expiresAt - Date.now()) / 1000),
      0
    );

    let phase: SessionPhase;
    if (remainingSeconds <= 0) {
      phase = 'expired';
    } else if (remainingSeconds <= warningSeconds) {
      phase = 'warning';
    } else {
      phase = 'active';
    }

    this.stateSubject.next({
      phase,
      sessionExpiresAt: new Date(expiresAt).toISOString(),
      warningSeconds,
      remainingSeconds,
    });

    // Once expired, stop the countdown; the popup drives the redirect.
    if (phase === 'expired') {
      this.tickSub?.unsubscribe();
      this.tickSub = null;
    }
  }

  ngOnDestroy(): void {
    this.stop();
  }
}
